import { USED_PARTS_ENDPOINT } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import CustomCircularProgress from "../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { Box, Typography, Divider } from "@mui/material";
import { basicInfoContainerStyle } from "../../../../styles/commonStyles";
import { useTranslation } from "react-i18next";
import { useHasPermission } from "../../../../contexts/auth";
import { formatDateTime, formatMoney, formatQuantity } from "../../../../utils";
import { useBreadcrumbs } from "../../../../components/ui/breadcrumps/breadcrumbsContext";
import EntityReferenceLink from "../../../../components/ui/entityReference/entityReferenceLink.component";
import { buildOrderedPartReferenceId } from "../../../../utils/entityRoutes";

type DetailRowProps = {
  label: string;
  children: ReactNode;
  noBorder?: boolean;
};

const DetailRow = ({ label, children, noBorder = false }: DetailRowProps) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "200px 1fr" },
      gap: { xs: 0.5, sm: 2 },
      alignItems: "start",
      py: 1.25,
      px: 1.5,
      borderBottom: noBorder ? "none" : "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ minHeight: "24px", display: "flex", alignItems: "center" }}>{children}</Box>
  </Box>
);

export const UsedPartBasicInfo = () => {
  const { id, usedPartId } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(USED_PARTS_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [usedPart, setUsedPart] = useState<any | null>(null);
  const canViewUsedParts = useHasPermission("service.view_usedpart");
  const canViewJobs = useHasPermission("service.view_job");
  const canViewOrderedParts = useHasPermission("service.view_orderedpart");

  useEffect(() => {
    const fetchUsedPart = async () => {
      if (id && usedPartId) {
        const response = await get(usedPartId);
        if (response.data) {
          setUsedPart(response.data);
          const usedPartLabel = (t("Used for") + " " + response.data.job_info.number) || "";
          setOverride(`/service/stock-status/${id}/used-parts/${usedPartId}`, usedPartLabel);
        }
      }
    };

    const handleUsedPartUpdated = () => {
      fetchUsedPart();
    };

    window.addEventListener("usedPartUpdated", handleUsedPartUpdated);
    fetchUsedPart();
    return () => {
      window.removeEventListener("usedPartUpdated", handleUsedPartUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!canViewUsedParts) {
    return null;
  }

  if (!usedPart) return <CustomCircularProgress />;

  return (
    <Box
      sx={basicInfoContainerStyle}
    >
      {/* Header Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {t("Used for")} {usedPart.job_info?.number || id}
        </Typography>
      </Box>

      <Divider />

      {/* Basic Information Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Basic Information")}
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <DetailRow label={t("From Order")}>
            <EntityReferenceLink
              entityType="orderedPart"
              entityId={
                usedPart.ordered_part_info
                  ? buildOrderedPartReferenceId(
                    usedPart.ordered_part_info.part_id,
                    usedPart.ordered_part_info.id
                  )
                  : undefined
              }
              label={usedPart.ordered_part_info?.number}
              canPreview={canViewOrderedParts}
            />
          </DetailRow>
          {usedPart.job_task && (
            <DetailRow label={t("Job")}>
              <EntityReferenceLink
                entityType="job"
                entityId={usedPart.job_info?.id}
                label={usedPart.job_info?.number}
                canPreview={canViewJobs}
              />
            </DetailRow>
          )}
          <DetailRow label={t("Quantity")}>
            <Typography variant="subtitle1">{formatQuantity(usedPart.quantity ?? 0)}</Typography>
          </DetailRow>
          <DetailRow label={t("Total Cost")} noBorder>
            <Typography variant="subtitle1">{formatMoney(usedPart.total ?? 0)}</Typography>
          </DetailRow>
        </Box>
      </Box>

      <Divider />

      {/* Metadata Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Metadata")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Used At")}:</strong> {formatDateTime(usedPart.updated_at)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
